import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isFuture, toDate } from 'date-fns'

import {
  SW_TAG_ENCRYPT_AND_SAVE_IMAGES,
  SW_TAG_GENERATE_AND_SAVE_CONTEXT,
  SW_TAG_GENERATE_AND_SAVE_MOMENTS_CHUNKS,
  SW_TAG_GENERATE_AND_SAVE_TAGS_AND_EMOTION,
} from '@/constants'
import {
  storeMomentImagesPayloads,
  storeMomentTagsAndEmotionsPayload,
  storeMomentsChunksPayload,
  storeUserContextPayload,
} from '@/local-db'
import {
  registerSyncs,
  fallbackSaveImages,
  fallbackSaveChunks,
  fallbackUpdateUserContext,
} from '@/service-worker'
import { useEncryptionKey } from '@/store'
import { useApi, useApiClient } from '@/trpc/react'
import { encrypt } from '@/utils'

import { UseCreateMomemntsArgs } from './type'

interface CreateMomentArgs {
  userContext: string
  title: string
  description: string
  images: File[]
  tags: string[]
  date: string
  zone: string
}

const generateRandomId = () => {
  return Math.floor(Math.random() * 1000000)
}

const useCreateMoments = ({ onSuccess }: UseCreateMomemntsArgs) => {
  const apiClient = useApiClient()
  const encryptionKey = useEncryptionKey()
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userContext,
      title,
      description,
      images,
      tags,
      date,
      zone,
    }: CreateMomentArgs) => {
      if (!encryptionKey) {
        throw new Error('No encryption key')
      }

      const encryptedTitle = await encrypt(title ?? '', encryptionKey)
      const encryptedDescription = await encrypt(
        description ?? '',
        encryptionKey,
      )
      const textToEmbed = `${title} ${description}`.trim()
      const finalDate = toDate(date).toISOString()
      const { id: momentId } = await apiClient.moments.create.mutate({
        moment: {
          title: encryptedTitle,
          momentEncrypted: encryptedDescription,
          date: finalDate,
          tags: [...tags],
          feelings: [],
          isLetter: isFuture(new Date(date)),
          category: [],
        },
        zone,
      })

      if (
        'serviceWorker' in navigator &&
        'SyncManager' in window &&
        navigator.serviceWorker.controller &&
        !navigator.serviceWorker.controller.scriptURL.includes('/push')
      ) {
        await Promise.all([
          storeUserContextPayload({
            id: generateRandomId(),
            encryptKey: encryptionKey,
            prevContext: userContext,
            userInput: textToEmbed,
          }),
          storeMomentsChunksPayload({
            id: generateRandomId(),
            encryptKey: encryptionKey,
            textToEmbed,
            momentId,
          }),
          storeMomentTagsAndEmotionsPayload({
            id: generateRandomId(),
            momentId,
            content: textToEmbed,
          }),
          storeMomentImagesPayloads(
            images.map((image, index) => ({
              id: generateRandomId(),
              momentId,
              media: image.slice(0, image.size, image.type),
              type: image.type,
              fileName: image.name,
              order: index,
              encryptKey: encryptionKey,
            })),
          ),
        ])
        registerSyncs({ tag: SW_TAG_GENERATE_AND_SAVE_CONTEXT })
        registerSyncs({ tag: SW_TAG_GENERATE_AND_SAVE_MOMENTS_CHUNKS })
        registerSyncs({ tag: SW_TAG_GENERATE_AND_SAVE_TAGS_AND_EMOTION })
        if (images.length > 0) {
          registerSyncs({ tag: SW_TAG_ENCRYPT_AND_SAVE_IMAGES })
        }
      } else {
        await Promise.all([
          fallbackUpdateUserContext({
            previousUserContext: userContext,
            input: textToEmbed,
            encryptKey: encryptionKey,
          }),
          fallbackSaveChunks({
            textToEmbed,
            momentId,
            encryptKey: encryptionKey,
          }),
          apiClient.moments.generateAndSaveTagsAndEmotion.mutate({
            id: momentId,
            content: textToEmbed,
          }),
          fallbackSaveImages({
            momentId,
            images,
            encryptKey: encryptionKey,
          }),
        ])
      }

      return momentId
    },
    onSettled: (data, error, _variables) => {
      if (error) {
        throw error
      }
      queryClient.invalidateQueries({
        queryKey: api.moments.pathKey(),
      })
      onSuccess?.(data ?? '')
    },
  })
}

export default useCreateMoments
